import { memo, useEffect, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Typography,
  CircularProgress,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NoContentCard from "src/reusable/Sidebar/NoContentCard";
import { SideBarScroll } from "src/views/assistant/components/ui/SideBarScroll";
import { getFileIconAndColor } from "src/reusable/Functions";
import { getFileExtension } from "src/views/cloudFiles/services/CloudFileServices";
import { useReportTemplateById } from "src/views/reports/ReportServices";
import { fetchEnrichedArtifacts } from "src/views/products/workflow/services/ArtifactEnrichmentService";

interface ReportTemplateTabProps {
  templateId: number | null;
}

export interface ResolvedTemplateLink {
  /** URL to navigate to when the template is clicked */
  url: string;
  /** Whether the link goes to a specific document or just the dataset viewer */
  isDocumentLink: boolean;
}

/**
 * Resolve the template artifact to a viewable document link.
 *
 * 1. If the artifact has an associated SDR → `/datasets/{datasetId}/document/{sdrId}`
 * 2. If no SDR but the template has a dataset_fk → `/datasets/{datasetFk}/viewer`
 * 3. Otherwise → null (no link available)
 */
export const resolveTemplateLink = async (
  artifactFk: number,
  datasetFk: number | null | undefined,
): Promise<ResolvedTemplateLink | null> => {
  try {
    const enriched = await fetchEnrichedArtifacts([artifactFk]);
    if (enriched.length > 0) {
      const { sdr_id, dataset_id } = enriched[0];
      if (sdr_id && dataset_id) {
        return {
          url: `/datasets/${dataset_id}/document/${sdr_id}`,
          isDocumentLink: true,
        };
      }
    }
  } catch (error) {
    console.warn(
      "[ReportTemplateTab] Failed to resolve artifact SDR, falling back to dataset link",
      error,
    );
  }

  // Fallback: link to the template's dataset viewer
  if (datasetFk) {
    return {
      url: `/datasets/${datasetFk}/viewer`,
      isDocumentLink: false,
    };
  }

  return null;
};

/**
 * ReportTemplateTab - Displays the report template used for proposal generation.
 *
 * Resolves the template_id → ReportTemplate → artifact → SDR to build a
 * clickable link to the document viewer. Falls back to the dataset viewer
 * if the artifact has no associated SDR.
 */
const ReportTemplateTab = memo(({ templateId }: ReportTemplateTabProps) => {
  const { data: template, isLoading: templateLoading } =
    useReportTemplateById(templateId);

  const [link, setLink] = useState<ResolvedTemplateLink | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);

  // Resolve the template artifact to a viewable link
  useEffect(() => {
    if (!template?.artifact_fk) {
      setLink(null);
      setLinkLoading(false);
      return;
    }

    let isMounted = true;
    setLinkLoading(true);

    resolveTemplateLink(template.artifact_fk, template.dataset_fk)
      .then((resolved) => {
        if (isMounted) {
          setLink(resolved);
          setLinkLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLinkLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [template?.artifact_fk, template?.dataset_fk]);

  if (!templateId) {
    return (
      <NoContentCard
        title="No report template"
        description="A report template will appear here once one is selected for report generation."
      />
    );
  }

  if (templateLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!template) {
    return (
      <NoContentCard
        title="Template not found"
        description="The selected report template could not be loaded."
      />
    );
  }

  const fileName = template.artifact_name || template.name || "Template";
  const fileExt = getFileExtension(fileName);
  const { icon } = getFileIconAndColor({ fileName, fontSize: 20 });
  const isClickable = link !== null && !linkLoading;

  const handleClick = () => {
    if (link) {
      window.open(link.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <SideBarScroll>
      <Box sx={{ px: 1, pt: 0.5 }}>
        <List disablePadding>
          <ListItemButton
            data-testid={`pg-template-item-${templateId}`}
            onClick={handleClick}
            disabled={!isClickable}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              "&:hover .open-icon": {
                opacity: 1,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {linkLoading ? <CircularProgress size={20} /> : icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  noWrap
                  title={template.name}
                  sx={{ fontWeight: 500 }}
                >
                  {template.name}
                </Typography>
              }
              secondary={
                <Box>
                  {template.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                      title={template.description}
                    >
                      {template.description}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.25,
                    }}
                  >
                    {fileExt && (
                      <Chip
                        label={fileExt}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>
                </Box>
              }
            />
            {isClickable && (
              <OpenInNewIcon
                className="open-icon"
                sx={{
                  fontSize: 16,
                  color: "text.secondary",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  ml: 1,
                }}
              />
            )}
          </ListItemButton>
        </List>
      </Box>
    </SideBarScroll>
  );
});

ReportTemplateTab.displayName = "ReportTemplateTab";

export default ReportTemplateTab;
